import { useEffect, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useMotionTemplate,
  animate,
  useReducedMotion,
} from "motion/react";

interface SendGlowProps {
  active: boolean;
}

/**
 * SendGlow - An iridescent shimmer overlay shown during the send flow.
 *
 * Uses Motion One (motion/react) for:
 * - Smooth mount/unmount with AnimatePresence
 * - Spring-based opacity fade
 * - MotionValue-driven gradient drift
 */
export function SendGlow({ active }: SendGlowProps) {
  const prefersReducedMotion = useReducedMotion();

  // If user prefers reduced motion, don't render anything
  if (prefersReducedMotion) {
    return null;
  }

  return (
    <AnimatePresence>
      {active && <SendGlowInner key="send-glow" />}
    </AnimatePresence>
  );
}

function SendGlowInner() {
  // Gradient circle positions (percentage) - animated for organic drift
  const pos1X = useMotionValue(20);
  const pos1Y = useMotionValue(25);
  const pos2X = useMotionValue(75);
  const pos2Y = useMotionValue(40);
  const pos3X = useMotionValue(55);
  const pos3Y = useMotionValue(80);

  // Breathing scale for the whole gradient
  const breatheScale = useMotionValue(1);
  const rotateDeg = useMotionValue(-0.6);

  // Static colors: purple, cyan, pink
  const background = useMotionTemplate`
    radial-gradient(circle at ${pos1X}% ${pos1Y}%, rgba(124, 58, 237, 0.85), rgba(124, 58, 237, 0) 50%),
    radial-gradient(circle at ${pos2X}% ${pos2Y}%, rgba(56, 189, 248, 0.78), rgba(56, 189, 248, 0) 52%),
    radial-gradient(circle at ${pos3X}% ${pos3Y}%, rgba(236, 72, 153, 0.75), rgba(236, 72, 153, 0) 54%)
  `;

  // Refs for animation controls to cancel on unmount
  const animationsRef = useRef<Array<{ stop: () => void }>>([]);

  useEffect(() => {
    // Position drift animation - slow and subtle (premium feel)
    const driftDuration = 2.1;

    // Purple circle: drifts from top-left area
    const pos1XAnim = animate(pos1X, [20, 42, 8, 20], {
      duration: driftDuration,
      repeat: Infinity,
      ease: "easeInOut",
    });

    const pos1YAnim = animate(pos1Y, [25, 10, 44, 25], {
      duration: driftDuration * 1.1,
      repeat: Infinity,
      ease: "easeInOut",
    });

    // Cyan circle: drifts from top-right area
    const pos2XAnim = animate(pos2X, [75, 56, 92, 75], {
      duration: driftDuration * 0.9,
      repeat: Infinity,
      ease: "easeInOut",
    });

    const pos2YAnim = animate(pos2Y, [40, 60, 24, 40], {
      duration: driftDuration * 1.2,
      repeat: Infinity,
      ease: "easeInOut",
    });

    // Pink circle: drifts from bottom-center area
    const pos3XAnim = animate(pos3X, [55, 34, 78, 55], {
      duration: driftDuration * 0.85,
      repeat: Infinity,
      ease: "easeInOut",
    });

    const pos3YAnim = animate(pos3Y, [80, 62, 94, 80], {
      duration: driftDuration * 1.15,
      repeat: Infinity,
      ease: "easeInOut",
    });

    // Breathing pulse - subtle scale oscillation
    const breatheAnim = animate(breatheScale, [1, 1.05, 1], {
      duration: 1.4,
      repeat: Infinity,
      ease: "easeInOut",
    });

    const rotateAnim = animate(rotateDeg, [-0.6, 0.6, -0.6], {
      duration: 1.8,
      repeat: Infinity,
      ease: "easeInOut",
    });

    animationsRef.current = [
      pos1XAnim,
      pos1YAnim,
      pos2XAnim,
      pos2YAnim,
      pos3XAnim,
      pos3YAnim,
      breatheAnim,
      rotateAnim,
    ];

    return () => {
      animationsRef.current.forEach((anim) => anim.stop());
      animationsRef.current = [];
    };
  }, [pos1X, pos1Y, pos2X, pos2Y, pos3X, pos3Y, breatheScale, rotateDeg]);

  return (
    <motion.div
      aria-hidden="true"
      className="pointer-events-none absolute z-0"
      style={{
        inset: "-14%",
        borderRadius: "28px",
        filter: "blur(16px)",
        background,
        scale: breatheScale,
        rotate: rotateDeg,
      }}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{
        opacity: 0.26,
      }}
      exit={{
        opacity: 0,
        scale: 1.05,
      }}
      transition={{
        opacity: { type: "spring", stiffness: 110, damping: 22, mass: 0.7 },
        scale: { type: "spring", stiffness: 90, damping: 18, mass: 0.9 },
      }}
    />
  );
}
